import React, { useEffect, useRef, useState } from 'react';
import { wavelengths } from '../../data/wavelengths';
import { useActiveStep } from '../../hooks/useActiveStep';
const maskArtwork = 'https://cdn.magicpatterns.com/uploads/kyRXj4knu1WdtQLrCRih3e/mask-hero-cutout-v2.png';
type Wavelength = typeof wavelengths[number];
function LightVisual({
  item,
  compact = false
}: {
  item: Wavelength;
  compact?: boolean;
}) {
  return <figure aria-label={`White AuraGlow mask with an illustrative ${item.name.toLowerCase()} light treatment`} className={`relative isolate flex items-center justify-center overflow-hidden ${compact ? 'h-[220px]' : 'h-full min-h-0'}`} style={{
    background: `radial-gradient(ellipse at 50% 60%, ${item.glow}AA, transparent 68%)`
  }} data-id="element-404" data-mp="3uzoy67zwsmfv">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-20" style={{
      backgroundImage: 'linear-gradient(#FFFFFF22 1px, transparent 1px), linear-gradient(90deg,#FFFFFF22 1px, transparent 1px)',
      backgroundSize: '56px 56px'
    }} data-id="element-405" data-mp="3bamw4o441au0" />
      <svg aria-hidden="true" viewBox="0 0 500 500" className="absolute h-full max-h-[540px] w-full max-w-[540px] opacity-40" fill="none" data-id="element-406" data-mp="2218j6pr37hc0">
        <circle cx="250" cy="250" r="180" stroke={item.color} strokeWidth="1" data-id="element-407" data-mp="2brle9bwj2swe" />
        <circle cx="250" cy="250" r="215" stroke={item.color} strokeWidth="1" strokeDasharray={item.invisible ? '3 10' : '1 0'} data-id="element-408" data-mp="2bto2pri18dz0" />
        <path d="M0 250 H500 M250 0 V500" stroke={item.color} opacity=".3" data-id="element-409" data-mp="1j35yuo9hi8kj" />
      </svg>
      <img src={maskArtwork} alt="" width="1226" height="1283" className={`relative z-10 block object-contain ${compact ? 'h-[190px] w-[230px]' : 'h-[82%] max-h-[455px] w-[90%]'}`} style={{
      filter: 'drop-shadow(0 20px 20px rgba(0,0,0,.28))'
    }} data-id="element-410" data-mp="1o1hdrdemz99m" />
      <figcaption className="absolute inset-x-4 bottom-3 z-20 flex items-center justify-between gap-3 text-[11px] text-white/80 lg:inset-x-6 lg:bottom-5 lg:text-[12px]" data-id="element-411" data-mp="1xxu4c2evx705">
        <span data-id="element-412" data-mp="39dr1uf0sgz8z">Wavelength illustration</span>
        <span data-id="element-413" data-mp="19cuaosfs7gea">{item.invisible ? 'Invisible near-infrared' : 'Visible light'}</span>
      </figcaption>
    </figure>;
}

/** Native scroll changes the selected wavelength; no wheel interception. */
export function WavelengthStory() {
  const {
    active,
    trackRef,
    stageRef,
    goToStep,
    isPinned,
    stickyTop,
    stageHeight,
    trackHeight
  } = useActiveStep(wavelengths.length, 'readable');
  const [mobileActive, setMobileActive] = useState(0);
  const mobileSteps = useRef<Array<HTMLLIElement | null>>([]);
  const mobileVisual = useRef<HTMLDivElement | null>(null);
  const selected = wavelengths[isPinned ? active : mobileActive];
  useEffect(() => {
    if (isPinned) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      // Match text entry to the actual pinned illustration, including header/review offsets.
      const trigger = (mobileVisual.current?.getBoundingClientRect().bottom ?? 0) + 56;
      let next = 0;
      mobileSteps.current.forEach((step, index) => {
        if (step && step.getBoundingClientRect().top <= trigger) next = index;
      });
      setMobileActive(previous => previous === next ? previous : next);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', schedule, {
      passive: true
    });
    window.addEventListener('resize', schedule, {
      passive: true
    });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [isPinned]);
  return <section id="wavelengths" aria-labelledby="wavelength-title" className="border-t border-line" data-id="element-414" data-mp="1k3sfiylt9iu">
      <div className="mx-auto max-w-page px-5 pb-10 pt-16 md:px-10 lg:pt-24" data-id="element-415" data-mp="6j6zta223rtz">
        <p className="text-[13px] font-medium uppercase tracking-[.12em] text-bronze-deep" data-id="element-416" data-mp="2mausgta1ey6z">The light behind the mask</p>
        <div className="mt-4 grid gap-6 lg:grid-cols-[1.2fr_1fr] lg:items-end" data-id="element-417" data-mp="2rbifz4z24wwg">
          <h2 id="wavelength-title" className="max-w-2xl font-display text-[42px] font-medium leading-[1.04] text-ink md:text-[58px]" data-id="element-418" data-mp="37hu2q48fj84y">Four Wavelengths.<br data-id="element-419" data-mp="32pkgozafqs7y" />One Mask.</h2>
          <div className="max-w-lg text-[16px] leading-relaxed text-ink-muted" data-id="element-420" data-mp="2xrse9bc5znpi">
            <p data-id="element-421" data-mp="2xpoen5latav1">Blue, red and two near-infrared wavelengths. Scroll to explore the light spectrum and the skincare research behind it.</p>
            <p className="mt-3 text-[14px]" data-id="element-422" data-mp="3qgzlqztljnmw">The mask combines these in two operating modes: M1 red + near-infrared, and M2 blue + near-infrared.</p>
          </div>
        </div>
        <dl className="mt-9 grid grid-cols-3 gap-4 border-t border-line pt-6" data-id="element-423" data-mp="3gia1x1i53t40">
          {[{
          value: '164',
          label: 'laser emitters'
        }, {
          value: '4',
          label: 'wavelengths'
        }, {
          value: '1064',
          label: 'nm near-infrared'
        }].map(item => <div key={item.label} data-id="element-424" data-mp="2510l6mqebf6e"><dd className="font-display text-[36px] font-medium leading-none text-ink md:text-[48px]" data-id="element-425" data-mp="2040eqapmwaer">{item.value}</dd><dt className="mt-2 text-[12px] text-ink-muted md:text-[14px]" data-id="element-426" data-mp="fm9fu5h9hku9">{item.label}</dt></div>)}
        </dl>
      </div>

      <div ref={trackRef} data-wavelength-track className="relative mx-auto max-w-page px-5 md:px-10" style={{
      display: isPinned ? 'block' : 'none',
      height: trackHeight
    }} data-id="element-427" data-mp="kkb0uvy8zu8d">
        <div ref={stageRef} data-wavelength-stage data-active-wavelength={selected.id} className="sticky overflow-hidden rounded-xl text-white" style={{
        top: stickyTop,
        height: stageHeight,
        backgroundColor: selected.background
      }} data-id="element-428" data-mp="ubdzlr3pfx4h">
          <div className="grid h-full grid-cols-[1fr_1.1fr] items-stretch" data-id="element-429" data-mp="139r2xnpkzuqw">
            <div className="flex min-h-0 flex-col justify-center px-8 py-9 xl:px-12" data-id="element-430" data-mp="18byh8jxvvmwe">
              <p className="text-[13px] uppercase tracking-[.12em]" style={{
              color: selected.color
            }} data-id="element-431" data-mp="1d8oy39hiimiv">0{active + 1} / 04 · {selected.name}</p>
              <p className="mt-5 font-display text-[64px] leading-none xl:text-[80px]" data-id="element-432" data-mp="3a4cxleks4jap">{selected.wavelength}<span className="ml-2 font-sans text-[18px] text-white/70" data-id="element-433" data-mp="3f37fmeaxa3tr">nm</span></p>
              <h3 className="mt-6 font-display text-[34px] font-medium leading-tight xl:text-[42px]" data-id="element-434" data-mp="370p03llfye56">{selected.title}</h3>
              <p className="mt-4 max-w-md text-[16px] leading-relaxed text-white/90" data-id="element-435" data-mp="3316fkqxj852v">{selected.body}</p>
              <p className="mt-3 max-w-md text-[13px] leading-relaxed text-white/70" data-id="element-436" data-mp="3bzi14ejjkcog">{selected.detail}</p>
              <div className="mt-7 grid grid-cols-4 gap-2" role="group" aria-label="Choose wavelength" data-id="element-437" data-mp="3pz19n8g9lq1a">
                {wavelengths.map((item, index) => <button type="button" key={item.id} onClick={() => goToStep(index)} aria-pressed={active === index} className="min-h-[48px] border-b-2 px-1 py-2 text-left text-[13px] font-medium focus-visible:outline-white" style={{
                borderColor: active === index ? item.color : '#FFFFFF40',
                color: active === index ? item.color : '#FFFFFFB3'
              }} data-id="element-438" data-mp="3lwwhyf9dn3ha" data-mpi={index}>
                  {item.wavelength}<span className="ml-1 text-[10px]" data-id="element-439" data-mp="8k2t1iuq4d5y">nm</span>
                </button>)}
              </div>
            </div>
            <LightVisual item={selected} data-id="element-440" data-mp="s2gu1tkk2hba" />
          </div>
        </div>
      </div>

      <div data-wavelength-mobile style={{
      display: isPinned ? 'none' : undefined
    }} data-id="element-441" data-mp="w5dja34lpbo7">
        <div ref={mobileVisual} data-wavelength-mobile-visual data-active-wavelength={selected.id} className="sticky top-[calc(var(--site-header-height)_+_var(--prototype-top-offset,0px)_+_1px)] z-20 text-white md:top-[calc(var(--site-header-height)_+_1px)]" style={{
        backgroundColor: selected.background
      }} data-id="element-442" data-mp="i5vp188jd67g">
          <p className="absolute left-5 top-4 z-20 text-[12px] font-semibold uppercase tracking-[.1em]" style={{
          color: selected.color
        }} data-id="element-443" data-mp="d9f3mlsq15y2">{selected.name} · {selected.wavelength} nm</p>
          <LightVisual item={selected} compact data-id="element-444" data-mp="4f8r3ialnkcf" />
        </div>
        <ol className="mx-auto max-w-page px-5 md:px-10" data-id="element-445" data-mp="3lmuejxuqxmtb">
          {wavelengths.map((item, index) => <li key={item.id} ref={element => {
          mobileSteps.current[index] = element;
        }} data-wavelength-mobile-step={item.id} className="flex min-h-[max(640px,85svh)] flex-col justify-center border-b border-line py-8" style={{
          scrollMarginTop: 'calc(var(--site-header-height) + var(--prototype-top-offset, 0px) + 240px)'
        }} data-id="element-446" data-mp="3qmgre66nx11l" data-mpi={index}>
            <p className="text-[12px] font-medium uppercase tracking-[.1em] text-bronze-deep" data-id="element-447" data-mp="108udkvq7417">0{index + 1} / 04 · {item.name} · {item.wavelength} nm</p>
            <h3 className="mt-3 font-display text-[32px] font-medium leading-tight text-ink" data-id="element-448" data-mp="3n5mcv3btd8qi">{item.title}</h3>
            <p className="mt-3 max-w-xl text-[16px] leading-relaxed text-ink-muted" data-id="element-449" data-mp="3idugud9w2asz">{item.body}</p>
            <p className="mt-3 max-w-xl text-[13px] leading-relaxed text-ink-muted" data-id="element-450" data-mp="3edu3yx2fu7o1">{item.detail}</p>
          </li>)}
        </ol>
      </div>
      <div className="mx-auto max-w-page px-5 pb-14 pt-7 md:px-10" data-id="element-451" data-mp="39c4qzbvnnjxb">
        <p className="max-w-3xl text-[13px] leading-relaxed text-ink-muted" data-id="element-452" data-mp="34ed1eoihg9wa">Light-therapy research varies by device and treatment. Results vary. Follow the mask’s manual and speak with your dermatologist about suitability.</p>
        <p className="mt-2 text-[13px] text-ink-muted" data-id="element-453" data-mp="2pe7fra2e4fnq">Learn more: <a href="https://www.aad.org/public/cosmetic/safety/red-light-therapy" target="_blank" rel="noreferrer" className="underline underline-offset-4 hover:text-ink" data-id="element-454" data-mp="2u9mrnwgu81hi">American Academy of Dermatology: red light</a> · <a href="https://www.aad.org/public/diseases/acne/derm-treat/lasers-lights" target="_blank" rel="noreferrer" className="underline underline-offset-4 hover:text-ink" data-id="element-455" data-mp="348lsisvd2sty">Light-based acne care</a></p>
      </div>
    </section>;
}